import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { Spinner } from 'common/spinners';
import _ from 'lodash';
import { useToast, Button, BaseCard, BaseCardBody, BaseCardHeader, PageSelection } from '@unite-us/app-components';
import { hasFeatureRole } from 'common/utils/Roles/hasFeatureRole';
import { fetchServiceCases } from 'actions/Case/Contact/Group';
import { findContactDocuments } from 'actions/Document';
import {
  fetchContactDocuments,
  renameContactDocument,
  removeDocumentFromContactResources,
  uploadAndAttachContactDocuments,
} from 'actions/Document/Contact/Group';
import RenameDocumentDialog from 'common/form/DocumentUploader/components/RenameDocumentDialog';
import UploadAndAttachDocuments from 'common/form/DocumentUploader/components/UploadAndAttachDocuments';
import ContactDocuments from 'src/components/Facesheet/Uploads/components/ContactDocuments';
import './Uploads.scss';

export class FacesheetUploads extends Component {
  constructor(props) {
    super(props);

    this.onInputChange = this.onInputChange.bind(this);
    this.onRenameDocument = this.onRenameDocument.bind(this);
    this.openRenameDialog = this.openRenameDialog.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.fetchServiceCases = this.fetchServiceCases.bind(this);
    this.removeFromContact = this.removeFromContact.bind(this);
    this.saveUploadedDocuments = this.saveUploadedDocuments.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleContactChange = this.handleContactChange.bind(this);
    this.syncPageNumberFromBackend = this.syncPageNumberFromBackend.bind(this);
    this.getWarningText = this.getWarningText.bind(this);
    this.getPaginationData = this.getPaginationData.bind(this);
    this.renderUploadButton = this.renderUploadButton.bind(this);
    this.renderContent = this.renderContent.bind(this);

    this.mounted = false;
    this.state = {
      editMode: false,
      documentSearch: props.searchedTerm,
      loading: false,
      currentDocument: {},
      pageNumber: 1,
      pageSize: 30,
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.fetchDocumentsOnly();
  }

  componentDidUpdate(prevProps) {
    if (!this.mounted) {
      return;
    }

    this.handleContactChange(prevProps);
    this.syncPageNumberFromBackend();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleContactChange(prevProps) {
    const { contact } = this.props;
    const prevContactId = prevProps.contact?.id;
    const currentContactId = contact?.id;

    if (prevContactId !== currentContactId) {
      this.setState({ pageNumber: 1 }, () => {
        this.fetchDocumentsOnly();
      });
    }
  }

  handlePageChange(newPageNumber) {
    if (this.mounted) {
      this.setState({ pageNumber: newPageNumber }, () => {
        this.fetchDocumentsOnly();
      });
    }
  }

  onInputChange(documentSearch) {
    if (this.mounted) {
      this.setState({ documentSearch });
    }
  }

  onRenameDocument(doc) {
    const { contact, groupId } = this.props;
    this.props.renameContactDocument({
      contactId: contact?.id,
      documentId: doc.id,
      groupId,
      title: doc.title,
    });
  }

  getPaginationData() {
    const { documents } = this.props;
    const { pageNumber } = this.state;
    const paging = documents?.paging || {};

    return {
      totalItemCount: paging.total_count || 0,
      totalPageCount: paging.total_pages || 1,
      currentPage: paging.current_page || pageNumber,
    };
  }

  getWarningText() {
    const { hasRestrictedUploadFlag } = this.props;
    return hasRestrictedUploadFlag ?
      [
        'Reminder: Unless you limit document visibility, all providers working with',
        'a client can access documents uploaded here. To upload documents specific to a referral',
        'or case (especially those with PHI), go to that referral/case record.'].join(' ') :
      [
        'Reminder: Documents uploaded to the Upload Tab are accessible',
        'by all providers working with a client. Please only upload documents specific to a',
        'referral or case (especially PHI) on that case/referral record.'].join(' ');
  }

  openRenameDialog(doc) {
    if (this.mounted) {
      this.setState({ editMode: true, currentDocument: doc }, () => {
        this.renameDocumentDialog.form.getWrappedInstance().dialog.openDialog()
          .then(() => {
            if (this.mounted) {
              this.setState({ editMode: false });
            }
          });
      });
    }
  }

  fetchData() {
    const { contact } = this.props;
    const contactId = contact?.id;
    const { pageNumber, pageSize } = this.state;

    if (!this.mounted || !contactId) {
      return;
    }

    this.setState({ loading: true });

    if (!this.props.findContactDocuments) {
      return;
    }

    // Fetch documents first, then service cases with delay to prevent Redux conflicts
    const documentsPromise = this.props.fetchContactDocuments({
      contactId,
      page: pageNumber,
      per: pageSize,
    });

    const serviceCasesPromise = new Promise((resolve, reject) => {
      setTimeout(() => {
        if (this.mounted) {
          this.fetchServiceCases()
            .then(resolve)
            .catch(reject);
        } else {
          reject(new Error('Component unmounted before service cases fetch'));
        }
      }, 50);
    });

    Promise.all([documentsPromise, serviceCasesPromise])
      .then(() => {
        if (this.mounted) {
          this.props.findContactDocuments(contactId, this.state.documentSearch);
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        if (this.mounted) {
          this.setState({ loading: false });
        }
      });
  }

  fetchDocumentsOnly() {
    const { contact } = this.props;
    const contactId = contact?.id;
    const { pageNumber, pageSize } = this.state;

    if (!this.mounted || !contactId) {
      return;
    }

    this.setState({ loading: true });

    if (!this.props.findContactDocuments) {
      return;
    }

    this.props.fetchContactDocuments({
      contactId,
      page: pageNumber,
      per: pageSize,
    })
      .then(() => {
        if (this.mounted) {
          this.props.findContactDocuments(contactId, this.state.documentSearch);
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        if (this.mounted) {
          this.setState({ loading: false });
        }
      });
  }

  fetchServiceCases() {
    const { groupId, contact } = this.props;
    const contactId = contact?.id;
    const contactCases = _.get(this.props, 'contactCases', []);

    if (_.isEmpty(contactCases) && contactId) {
      return this.props.fetchServiceCases(groupId, contactId);
    }
    return Promise.resolve();
  }

  removeFromContact(doc = {}) {
    const { contact, addToast } = this.props;
    return this.props.removeDocumentFromContactResources({
      contactId: contact?.id,
      documentId: doc.id,
      addToast,
    });
  }

  saveUploadedDocuments({ uploadedDocuments, privateToOrg, privateToRole }) {
    const { contact, addToast } = this.props;
    const additionalData = [
      {
        key: 'privateToOrg',
        value: privateToOrg,
      },
      {
        key: 'privateToRole',
        value: privateToRole,
      },
    ];
    if (!_.isEmpty(uploadedDocuments) && contact?.id) {
      this.props.uploadAndAttachContactDocuments({
        contactId: contact.id,
        uploadedDocuments,
        additionalData,
      }).then(() => {
        if (this.mounted) {
          this.setState({ pageNumber: 1 }, () => {
            this.fetchData();
          });
          addToast({
            message: 'Documents uploaded',
            type: 'success',
          });
        }
      });
    }
  }

  syncPageNumberFromBackend() {
    const { documents } = this.props;
    const paging = documents?.paging || {};
    const currentPageFromBackend = paging.current_page;
    const { pageNumber } = this.state;

    if (currentPageFromBackend && currentPageFromBackend !== pageNumber) {
      this.setState({ pageNumber: currentPageFromBackend });
    }
  }

  renderContent() {
    const { documents, shouldPortalizeDialogs } = this.props;
    const { currentDocument, editMode } = this.state;
    const { totalItemCount, totalPageCount, currentPage } = this.getPaginationData();

    return (
      <div className="uploads__body">
        <ContactDocuments
          documents={documents.data || []}
          onRemove={this.removeFromContact}
          onRename={this.openRenameDialog}
          shouldPortalizeDialogs={shouldPortalizeDialogs}
        />

        <RenameDocumentDialog
          doc={currentDocument}
          ref={(c) => { this.renameDocumentDialog = c; }}
          onRenameDocument={this.onRenameDocument}
          dialogOpen={editMode}
          shouldPortalizeDialogs={shouldPortalizeDialogs}
        />

        {totalItemCount > 0 && (
          <div className="mt-6">
            <PageSelection
              pageNumber={currentPage}
              setPageNumber={this.handlePageChange}
              totalPageCount={totalPageCount}
            />
          </div>
        )}
      </div>
    );
  }

  renderUploadButton() {
    const { styles, hasSocialCareNavigationRole, hideUploadButton } = this.props;
    if (hideUploadButton) {
      return null;
    }

    const warningText = this.getWarningText();

    return (
      <UploadAndAttachDocuments
        anchorEl={(
          <Button
            id="upload-document-btn"
            label="Upload Document"
            className="uploads__upload-btn"
            labelStyle={_.get(styles, 'uploadDocButton', {})}
            primary
          />
        )}
        dropzoneName="contactDocuments"
        onAttachDocuments={this.saveUploadedDocuments}
        warningText={warningText}
        showDocumentVisibility
        hasSocialCareNavigationRole={hasSocialCareNavigationRole}
      />
    );
  }

  render() {
    const { loading } = this.state;

    return (
      <BaseCard className="uploads">
        <BaseCardHeader title="Client Record Uploads">
          {this.renderUploadButton()}
        </BaseCardHeader>
        <BaseCardBody withPadding>
          {loading ? <Spinner /> : this.renderContent()}
        </BaseCardBody>
      </BaseCard>
    );
  }
}

FacesheetUploads.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  documents: PropTypes.shape({
    data: PropTypes.array,
    paging: PropTypes.shape({
      current_page: PropTypes.number,
      next_page: PropTypes.number,
      prev_page: PropTypes.number,
      per: PropTypes.number,
      total_count: PropTypes.number,
      total_pages: PropTypes.number,
    }),
  }),
  fetchContactDocuments: PropTypes.func.isRequired,
  fetchServiceCases: PropTypes.func.isRequired,
  findContactDocuments: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  params: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  removeDocumentFromContactResources: PropTypes.func.isRequired,
  renameContactDocument: PropTypes.func.isRequired,
  searchedTerm: PropTypes.string.isRequired,
  styles: PropTypes.object,
  uploadAndAttachContactDocuments: PropTypes.func.isRequired,
  hasSocialCareNavigationRole: PropTypes.bool,
  hasRestrictedUploadFlag: PropTypes.bool,
  shouldPortalizeDialogs: PropTypes.bool,
  hideUploadButton: PropTypes.bool,
  addToast: PropTypes.func.isRequired,
};

FacesheetUploads.defaultProps = {
  documents: {
    data: [],
  },
  styles: {
    uploadDocButton: {
      textTransform: 'none',
    },
  },
  hasSocialCareNavigationRole: false,
  hasRestrictedUploadFlag: false,
  shouldPortalizeDialogs: false,
  hideUploadButton: false,
};

/**
 * @param { object } - contactDocuments - data from the contact Documents
 * @param { object } - OwnProps - Props receieved from the parent (used to get the contactId)
 * @return { object } - document object with its array of documents && paging object respectively
 */
const generateDocuments = ({ contactDocuments, contactId, ownProps }) => {
  if (_.isEmpty(contactDocuments)) {
    return {};
  }
  if (contactDocuments[contactId] && contactDocuments[contactId].searchedWord) {
    const filteredDocumentsObject = _.merge({}, contactDocuments);
    const documentSearchRegex = new RegExp(contactDocuments[contactId].searchedWord, 'gi');
    const filteredDocuments = contactDocuments[contactId].data.filter((doc) => (doc.title.match(documentSearchRegex)));
    filteredDocumentsObject[contactId].data = filteredDocuments;
    return filteredDocuments.length > 0 ? filteredDocumentsObject[contactId] : contactDocuments[contactId];
  }
  return contactDocuments[ownProps.params.id];
};

// Reselect selectors for memoization
const getContactDocuments = (state) => state.contactDocuments;
const getContactId = (state, ownProps) => ownProps.contactId || _.get(ownProps, 'params.id');
const getOwnProps = (state, ownProps) => ownProps;
const getContacts = (state) => state.contacts.contacts;
const getServiceCases = (state) => state.serviceCase;
const getEmployeeRoles = (state) => _.get(state, 'globalState.currentEmployee.roles', []);

const getContact = createSelector(
  [getContacts, getContactId, getOwnProps],
  (contacts, contactId, ownProps) => _.find(contacts, { id: contactId }) || ownProps.contact,
);

const getContactCases = createSelector(
  [getServiceCases, getContactId],
  (serviceCases, contactId) => {
    const contactCaseObj = _.get(serviceCases, contactId);
    return _.get(contactCaseObj, 'data', []);
  },
);

const getHasSocialCareNavigationRole = createSelector(
  [getEmployeeRoles],
  (employeeRoles) => hasFeatureRole({
    employeeRoles,
    targetRoleKey: 'social_care_navigation',
  }),
);

const getFilteredDocuments = createSelector(
  [getContactDocuments, getContactId, getOwnProps],
  (contactDocuments, contactId, ownProps) => generateDocuments({
    contactDocuments,
    contactId,
    ownProps,
  }),
);

function mapStateToProps(state, ownProps) {
  const contact = getContact(state, ownProps);
  const filteredDocuments = getFilteredDocuments(state, ownProps);
  const contactCases = getContactCases(state, ownProps);
  const hasSocialCareNavigationRole = getHasSocialCareNavigationRole(state, ownProps);

  return {
    groupId: state.session.groupId,
    documents: filteredDocuments,
    contact,
    searchedWord: state.contactDocuments.searchedWord,
    searchedTerm: _.get(ownProps, 'location.query.s', ''),
    contactCases,
    hasSocialCareNavigationRole,
  };
}

const ConnectedFacesheetUploads = connect(mapStateToProps, {
  fetchContactDocuments,
  fetchServiceCases,
  findContactDocuments,
  removeDocumentFromContactResources,
  renameContactDocument,
  uploadAndAttachContactDocuments,
})(FacesheetUploads);

// Wrapper component that provides useToast hook to the class component
function FacesheetUploadsWithToast(props) {
  const { addToast } = useToast();

  return <ConnectedFacesheetUploads {...props} addToast={addToast} />;
}

export default FacesheetUploadsWithToast;
